import { isPlainObject, isString } from 'remeda'
import type { BannerTranslations } from './types.js'

// Constants -------------------------------------------------------------------

const STORAGE_KEY = 'keruj:browser-warning-dismissed'
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg'

const DEFAULT_TRANSLATIONS: BannerTranslations = {
  title: 'Your browser is outdated',
  message: 'Some features may not work correctly.',
  dismiss: 'Dismiss'
}

// Private functions -----------------------------------------------------------

// Accept only complete text translations from the JSON boundary.
const parseTranslations = (value: unknown): BannerTranslations | null =>
  isPlainObject(value) &&
  isString(value.title) &&
  isString(value.message) &&
  isString(value.dismiss)
    ? { title: value.title, message: value.message, dismiss: value.dismiss }
    : null

// Use the default warning when server translations are absent or invalid.
const getTranslations = (): BannerTranslations => {
  const text = document.querySelector('#browser-support-i18n')?.textContent
  if (!text) {
    return DEFAULT_TRANSLATIONS
  }

  try {
    const parsed: unknown = JSON.parse(text)

    return parseTranslations(parsed) ?? DEFAULT_TRANSLATIONS
  } catch {
    return DEFAULT_TRANSLATIONS
  }
}

// Remember dismissal for the current browser session.
const setDismissed = (): void => sessionStorage.setItem(STORAGE_KEY, 'true')

// Remove the warning after its exit animation completes.
const handleDismiss = (banner: HTMLElement): void => {
  banner.classList.replace('slideInDown', 'slideOutUp')
  banner.addEventListener('animationend', () => banner.remove(), { once: true })
  setDismissed()
}

// Render translated content as text, never as HTML.
const createMessage = ({
  title,
  message
}: BannerTranslations): HTMLParagraphElement => {
  const heading = document.createElement('strong')
  heading.textContent = `${title}.`

  const paragraph = document.createElement('p')
  paragraph.className = 'text-sm text-amber-800 text-center'
  paragraph.append(heading, ` ${message}`)

  return paragraph
}

// Keep the dismiss control independent of custom-element support.
const createDismissButton = (label: string): HTMLButtonElement => {
  const button = document.createElement('button')
  button.type = 'button'
  button.className =
    'shrink-0 p-1 text-amber-600 hover:text-amber-800 transition-colors'
  button.setAttribute('aria-label', label)

  const icon = document.createElementNS(SVG_NAMESPACE, 'svg')
  icon.setAttribute('class', 'w-5 h-5')
  icon.setAttribute('fill', 'none')
  icon.setAttribute('stroke', 'currentColor')
  icon.setAttribute('viewBox', '0 0 24 24')
  icon.setAttribute('aria-hidden', 'true')
  icon.setAttribute('focusable', 'false')

  const path = document.createElementNS(SVG_NAMESPACE, 'path')
  path.setAttribute('stroke-linecap', 'round')
  path.setAttribute('stroke-linejoin', 'round')
  path.setAttribute('stroke-width', '2')
  path.setAttribute('d', 'M6 18L18 6M6 6l12 12')
  icon.append(path)
  button.append(icon)

  return button
}

// Assemble the warning and attach dismissal to its owned button.
const createBannerElement = (translations: BannerTranslations): HTMLElement => {
  const banner = document.createElement('div')
  banner.id = 'browser-support-banner'
  banner.role = 'alert'
  banner.className = [
    'fixed top-0 inset-x-0 z-[70]',
    'bg-amber-50 border-b border-amber-200',
    'animated slideInDown'
  ].join(' ')

  const content = document.createElement('div')
  content.className =
    'max-w-screen-xl mx-auto px-4 py-3 flex items-center justify-center gap-4'
  const button = createDismissButton(translations.dismiss)
  button.addEventListener('click', () => handleDismiss(banner))
  content.append(createMessage(translations), button)
  banner.append(content)

  return banner
}

// Public functions ------------------------------------------------------------

// Read whether the warning was dismissed in this browser session.
export const isDismissed = (): boolean =>
  sessionStorage.getItem(STORAGE_KEY) === 'true'

// Show the warning using validated server text or the default translations.
export const showBanner = (): void => {
  document.body.prepend(createBannerElement(getTranslations()))
}
